import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { SIZE_LABELS, sizeFromHours, type SizeThresholds } from '@rundown/domain';
import { EmptyState, LoadMore } from '../../components';
import { formatDateTime, formatHours } from '../../lib/format';
import { cx } from '../../lib/cx';
import { useRequiredWorkspace, useSizeThresholds } from '../workspace/useWorkspace';
import { isInternalRequest, reviewQueueInfiniteQueryOptions, type RequestRow } from '../requests/api';
import { PRIORITY_LABELS, TYPE_LABELS, TYPE_TONES } from '../requests/labels';
import { planChangesQueryOptions, type PlanChange } from '../sprints/api';

/**
 * Review — what needs a decision from Noorix.
 *
 * This was a screen of its own (/review) until it became the second tab on
 * Home. Nothing about the queue changed: it was a screen you opened every
 * morning, found from the same place you post from, and then went back to
 * Home anyway — so it now sits beside Posts instead of one click away, and the
 * rail is one item shorter for it.
 *
 * Deliberately a list of links rather than a list of Confirm buttons.
 * Confirming from here would mean agreeing to a classification without having
 * read the request, and the type on a request is a billing classification: a
 * Bug in delivered work is retainer, a Change consumes the client's included
 * units. The decision lives on the request screen, next to the fields it is
 * about and the conversation that produced it.
 *
 * What the row shows is therefore chosen to answer "is this worth opening
 * next": who raised it, when, what was proposed, and the first lines of what
 * they actually wrote.
 */

const TONE_CHIP: Record<string, string> = {
  neutral: 'bg-surface-3 text-muted',
  accent: 'bg-accent-tint text-accent',
  success: 'bg-success-tint text-success',
  warn: 'bg-warn-tint text-warn',
  danger: 'bg-danger-tint text-danger',
  violet: 'bg-violet-tint text-violet',
};

function proposalOf(request: RequestRow, sizeThresholds: SizeThresholds): string {
  const parts = [TYPE_LABELS[request.type]];
  if (request.projectName !== null) parts.push(request.projectName);
  parts.push(PRIORITY_LABELS[request.priority]);
  parts.push(
    request.approxHours > 0
      ? `${SIZE_LABELS[sizeFromHours(request.approxHours, sizeThresholds)]} ~${formatHours(request.approxHours)}`
      : 'Needs analysis',
  );
  return parts.join(' · ');
}

function ReviewRow({ request }: { request: RequestRow }) {
  const internal = isInternalRequest(request) ? request : null;
  const sizeThresholds = useSizeThresholds();
  // Unclassified is the honest label until intake AI exists (M5): a request
  // posted through the ask box arrives with column defaults, and calling that
  // "AI classified" would dress a default up as a judgement.
  const unclassified = request.projectName === null && request.approxHours === 0;

  return (
    <Link
      to={`/t/${request.id}`}
      className="block rounded-xl border border-line px-[1.125rem] py-4 hover:border-line-stronger hover:bg-surface"
    >
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <span
          className={cx(
            'flex-none rounded-full px-2 py-0.5 fs-11 font-bold whitespace-nowrap',
            TONE_CHIP[TYPE_TONES[request.type]] ?? TONE_CHIP['neutral'],
          )}
        >
          {unclassified ? 'Unclassified' : TYPE_LABELS[request.type]}
        </span>
        <span className="fs-12 whitespace-nowrap text-muted">
          #{request.number} · {request.requesterName ?? 'Unknown'} · {formatDateTime(request.createdAt)}
        </span>
        {internal !== null && !internal.visibleToClient && (
          <span className="flex-none rounded-full bg-violet-tint px-2 py-0.5 fs-11 font-semibold text-violet">
            Internal
          </span>
        )}
      </div>

      <div className="mb-1.5 fs-15 font-bold text-ink">{request.title}</div>

      {request.descriptionText !== '' && (
        <p className="mb-3 line-clamp-3 fs-13 leading-relaxed text-ink-soft">{request.descriptionText}</p>
      )}

      <div className="flex flex-wrap items-center gap-2 fs-11 text-muted">
        <span>{proposalOf(request, sizeThresholds)}</span>
        <span className="ml-auto font-semibold whitespace-nowrap text-accent">Open →</span>
      </div>
    </Link>
  );
}

/**
 * A change of plan waiting for a decision. Like a request, it is decided where
 * it lives — on its sprint, beside the scope and the hotfix load it would
 * change — so this is a link, not an Agree button.
 */
function PlanChangeRow({ change }: { change: PlanChange }) {
  const parts = [
    change.add !== null ? `bring in #${change.add.number ?? '—'} ${change.add.title}` : null,
    change.remove !== null ? `take out #${change.remove.number ?? '—'} ${change.remove.title}` : null,
  ].filter((part): part is string => part !== null);
  return (
    <Link
      to={`/sprints?sprint=${change.sprintId}#plan-change-${change.id}`}
      className="block rounded-xl border border-warn-border px-[1.125rem] py-4 hover:bg-surface"
    >
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <span className="flex-none rounded-full bg-warn-tint px-2 py-0.5 fs-11 font-bold whitespace-nowrap text-warn">
          Change of plan
        </span>
        <span className="fs-12 whitespace-nowrap text-muted">
          {change.sprintName} · {change.raisedByName ?? 'Unknown'} · {formatDateTime(change.raisedAt)}
        </span>
      </div>
      <div className="mb-1.5 fs-15 font-bold text-ink first-letter:uppercase">{parts.join(', ')}</div>
      <p className="mb-3 line-clamp-2 fs-13 leading-relaxed text-ink-soft">{change.reason}</p>
      <div className="flex flex-wrap items-center gap-2 fs-11 text-muted">
        <span>
          {change.remove === null
            ? 'Nothing comes out — the sprint may run over.'
            : 'A swap inside a locked sprint.'}{' '}
          Agree or decline on the sprint.
        </span>
        <span className="ml-auto font-semibold whitespace-nowrap text-accent">Open →</span>
      </div>
    </Link>
  );
}

/**
 * How many things are waiting, for the tab beside Posts.
 *
 * Reads the same two queries the list does, so mounting both costs one fetch
 * of each — the cache is what they share, and the badge can never disagree
 * with the list under it. `more` is true when the queue has pages nobody has
 * asked for yet, which is the difference between "8" and "8+".
 */
export function useReviewCount(): { readonly count: number; readonly more: boolean } {
  const { workspace } = useRequiredWorkspace();
  const queue = useInfiniteQuery(reviewQueueInfiniteQueryOptions(workspace.id));
  const planChanges = useQuery(planChangesQueryOptions(workspace.id));

  const waiting = queue.data?.pages.reduce((total, page) => total + page.items.length, 0) ?? 0;
  const open = (planChanges.data ?? []).filter((change) => change.status === 'open').length;

  return { count: waiting + open, more: queue.hasNextPage };
}

export function ReviewList() {
  const { workspace } = useRequiredWorkspace();
  const query = useInfiniteQuery(reviewQueueInfiniteQueryOptions(workspace.id));
  // One queue, flattened across pages: the oldest unreviewed request is the one
  // that has been waiting longest, and a page boundary says nothing about that.
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];
  const planChanges = (useQuery(planChangesQueryOptions(workspace.id)).data ?? []).filter(
    (change) => change.status === 'open',
  );

  if (query.isPending) return <p className="fs-12 text-muted">Loading…</p>;
  if (query.error !== null) {
    return <EmptyState title="Could not load the queue" description={query.error.message} />;
  }

  if (items.length === 0 && planChanges.length === 0) {
    return (
      <EmptyState
        title="Nothing waiting"
        description="Every request has been reviewed. New posts land here as they arrive."
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {planChanges.map((change) => (
        <PlanChangeRow key={change.id} change={change} />
      ))}
      {items.map((request) => (
        <ReviewRow key={request.id} request={request} />
      ))}
      <LoadMore
        hasMore={query.hasNextPage}
        isLoading={query.isFetchingNextPage}
        onLoadMore={() => void query.fetchNextPage()}
        className="pt-1"
      />
    </div>
  );
}
